import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import {
  ProjectProgress,
  TrygroupFactorAnalysis,
  TrygroupFactorAnalysisStatus,
  TrygroupPersonalAnalysis,
  TrygroupPersonalAnalysisService,
} from '../../../services/trygroup-dashboard/trygroup-personal-analysis.service'
import { RoleplayStore } from '../../../stores/roleplay.store'
import { UserStore } from '../../../stores/user.store'
import {
  getFactorRankLabel as resolveFactorRankLabel,
  getKaIdentifier as resolveKaIdentifier,
} from './ka-labels'
import {
  getPatternTypeLabel as resolvePatternTypeLabel,
  isPriorityHitCount as resolveIsPriorityHitCount,
} from './key-action-diagnosis-table/key-action-diagnosis'
import { TrygroupKeyActionDiagnosisTableComponent } from './key-action-diagnosis-table/trygroup-key-action-diagnosis-table.component'
import { TrygroupProjectProgressChartComponent } from './project-progress-chart/trygroup-project-progress-chart.component'
import { TrygroupScoreTrendChartComponent } from './score-trend-chart/trygroup-score-trend-chart.component'

const DAY_MS = 24 * 60 * 60 * 1000
const JST_OFFSET_MS = 9 * 60 * 60 * 1000
const GOOD_EXAMPLES_DISPLAY_LIMIT = 3

type FactorAnalysisViewModel = {
  factorAnalysis: TrygroupFactorAnalysis
  visiblePatterns: TrygroupFactorAnalysis['patternSummaries']
}

@UntilDestroy()
@Component({
  selector: 'app-trygroup-member-analysis-page',
  standalone: true,
  imports: [
    TrygroupScoreTrendChartComponent,
    TrygroupProjectProgressChartComponent,
    TrygroupKeyActionDiagnosisTableComponent,
  ],
  template: `
    <section class="member-analysis-page">
      @if (isLoading) {
        <div class="status-panel">読み込み中...</div>
      } @else if (errorMessage) {
        <div class="status-panel error" role="alert" aria-live="assertive">
          {{ errorMessage }}
        </div>
      } @else if (analysis != null) {
        @if (analysis.summary) {
          <section class="teacher-summary">
            <div class="summary-card summary-profile">
              <p class="summary-label">
                {{ analysis.summary.userCategoryName }}
              </p>
              <p class="summary-name">{{ analysis.summary.teacherName }}</p>
            </div>
            <div class="summary-card summary-score">
              <div class="summary-card-header">
                <p class="summary-label">面入スコア</p>
                @if (
                  analysis.summary.targetScore != null &&
                  analysis.summary.overallScore != null &&
                  analysis.summary.targetScore - analysis.summary.overallScore >
                    0
                ) {
                  <span class="score-gap-badge">
                    目標まで あと
                    {{
                      formatScore(
                        analysis.summary.targetScore -
                          analysis.summary.overallScore
                      )
                    }}
                    点
                  </span>
                }
              </div>
              @if (analysis.summary.overallScore != null) {
                <p class="summary-big-number">
                  {{ formatScore(analysis.summary.overallScore) }}
                  <span class="summary-unit">点</span>
                  @if (analysis.summary.targetScore != null) {
                    <span class="summary-target">
                      / 目標 {{ formatScore(analysis.summary.targetScore) }}
                    </span>
                  }
                </p>
              } @else {
                <p class="summary-empty">データなし</p>
              }
            </div>
            <div class="summary-card summary-kpi">
              <div class="summary-card-header">
                <p class="summary-label">ビジネスKPI: 面入率</p>
                @if (
                  analysis.summary.targetAdmissionRate != null &&
                  analysis.summary.admissionRate != null &&
                  analysis.summary.targetAdmissionRate -
                    analysis.summary.admissionRate >
                    0
                ) {
                  <span class="score-gap-badge">
                    目標まで あと
                    {{
                      formatPercent(
                        analysis.summary.targetAdmissionRate -
                          analysis.summary.admissionRate
                      )
                    }}
                    %
                  </span>
                }
              </div>
              @if (analysis.summary.admissionRate != null) {
                <p class="summary-big-number">
                  {{ formatPercent(analysis.summary.admissionRate) }}
                  <span class="summary-unit">%</span>
                  @if (analysis.summary.targetAdmissionRate != null) {
                    <span class="summary-target">
                      / 目標
                      {{ formatPercent(analysis.summary.targetAdmissionRate) }}
                      %
                    </span>
                  }
                </p>
              } @else {
                <p class="summary-empty">データなし</p>
              }
            </div>
          </section>
        }

        @if (factorAnalysisViewModels.length > 0) {
          <section class="two-column-section">
            <div class="two-col-left">
              <section class="factor-section">
                <h2 class="factor-title">
                  達成するのに重要なキーアクションの因子
                </h2>
                <p class="factor-description">
                  過去の商談データとトップパフォーマーの差異をAIが分析し、あなたが目標達成に向けて「<strong>今、最も優先して改善すべきアクション</strong>」を特定しました。
                </p>
                @if (analysis.radarChart) {
                  @for (
                    factor of analysis.radarChart.priorityFactors.slice(0, 2);
                    track factor.rank
                  ) {
                    <div
                      class="factor-card"
                      [class.factor-card-primary]="factor.rank === 1"
                      [class.factor-card-secondary]="factor.rank === 2"
                    >
                      <div class="factor-card-left">
                        <span
                          class="factor-rank-badge"
                          [class.factor-rank-primary]="factor.rank === 1"
                          [class.factor-rank-secondary]="factor.rank === 2"
                        >
                          {{
                            factor.rank === 1 ? '第一因子 (最優先)' : '第二因子'
                          }}
                        </span>
                        <p class="factor-ka-name">
                          {{ getKaIdentifier(factor.kaId)
                          }}{{ factor.kaName || '' }}
                        </p>
                      </div>
                      <div class="factor-card-right">
                        <span class="factor-score-label">現状スコア</span>
                        <p class="factor-score-value">
                          {{ getKaScore(factor.kaId) }}
                          <span class="factor-score-max">/ 5.0</span>
                        </p>
                      </div>
                    </div>
                  }
                }
              </section>
            </div>

            <div class="two-col-right">
              @if (analysis.scoreTrend) {
                <section class="score-trend-section">
                  <h2 class="score-trend-title">
                    全キーアクション 詳細推移 (過去{{
                      analysis.scoreTrend.dataPoints.length
                    }}回)
                  </h2>
                  <app-trygroup-score-trend-chart
                    [scoreTrend]="analysis.scoreTrend"
                  />
                  <p class="score-trend-caption">
                    各アクションの質(1-5)の揺らぎと、総合スコア(0-100)のトレンドを確認できます。
                  </p>
                </section>
              } @else {
                <section class="coming-soon-section">
                  <h2 class="coming-soon-title">全キーアクション 詳細推移</h2>
                  <div class="coming-soon-body">
                    <p class="coming-soon-text">スコアデータがまだありません</p>
                  </div>
                </section>
              }
            </div>
          </section>

          @if (analysis.projectProgress) {
            <section class="project-progress-section">
              <h2 class="project-progress-title">
                面入スコア成長予測 予実推移 (Day
                {{ calculateDayN(analysis.projectProgress) }} / 残り
                {{ calculateRemainingDays(analysis.projectProgress) }} 日)
              </h2>
              <p class="project-progress-subtitle">
                目標達成（{{
                  analysis.projectProgress.targetScore.toFixed(1)
                }}点）に向けた軌跡
              </p>
              <app-trygroup-project-progress-chart
                [projectProgress]="analysis.projectProgress"
              />
            </section>
          } @else {
            <section class="project-progress-section coming-soon-section">
              <h2 class="project-progress-title">
                面入スコア成長予測 予実推移
              </h2>
              <div class="coming-soon-body">
                <span class="coming-soon-badge">
                  Coming Soon (順次リリース予定)
                </span>
                <p class="coming-soon-label">着地予測トレンド</p>
                <p class="coming-soon-text">
                  目標達成に向けた軌跡と、直近の成長ペースに基づく最終着地予測（AI予測）が追加されます
                </p>
              </div>
            </section>
          }

          <div class="bridge-guide">
            <p class="bridge-text">
              まずキーアクション分解診断でNG状態の全体像を確認し、優先度の高いNGを下の詳細で確認しましょう
            </p>
            <span class="bridge-arrow">↓</span>
          </div>

          @if (diagnosisFactorAnalyses.length > 0) {
            <app-trygroup-key-action-diagnosis-table
              [factorAnalyses]="diagnosisFactorAnalyses"
            />
          }

          <section class="ng-analysis-section">
            <div class="ng-analysis-header">
              <span class="ng-analysis-dot"></span>
              <div>
                <h2 class="ng-analysis-title">
                  直近5回のオリエンで多く見られるNGアクション一覧
                </h2>
                <p class="ng-analysis-subtitle">
                  直近の商談録音AI解析に基づく、優先強化因子ごとのNG行動（第一因子は最大3つ、第二因子は最大2つをピックアップ）
                </p>
              </div>
            </div>

            @for (
              factorAnalysisViewModel of factorAnalysisViewModels;
              track factorAnalysisViewModel.factorAnalysis.rank
            ) {
              <div class="factor-group">
                <div class="factor-group-header">
                  <span
                    class="factor-group-badge"
                    [class.factor-rank-primary]="
                      factorAnalysisViewModel.factorAnalysis.rank === 1
                    "
                    [class.factor-rank-secondary]="
                      factorAnalysisViewModel.factorAnalysis.rank === 2
                    "
                  >
                    {{
                      getFactorRankLabel(
                        factorAnalysisViewModel.factorAnalysis.rank
                      )
                    }}
                  </span>
                  <span class="factor-group-name">
                    {{
                      getKaIdentifier(
                        factorAnalysisViewModel.factorAnalysis.kaId
                      )
                    }}
                    @if (
                      factorAnalysisViewModel.factorAnalysis.kaDefinition !=
                      null
                    ) {
                      {{
                        factorAnalysisViewModel.factorAnalysis.kaDefinition
                          .kaName
                      }}
                    }
                  </span>
                </div>

                @if (
                  factorAnalysisViewModel.factorAnalysis.status ===
                  FactorAnalysisStatus.NoKaDefinition
                ) {
                  <div class="status-panel">
                    {{
                      formatNoKaDefinitionMessage(
                        factorAnalysisViewModel.factorAnalysis
                      )
                    }}
                  </div>
                } @else if (
                  factorAnalysisViewModel.factorAnalysis.status ===
                  FactorAnalysisStatus.InsufficientData
                ) {
                  <div class="status-panel">
                    分析データがまだありません。商談の評価が蓄積されるとここに結果が表示されます。
                  </div>
                } @else if (
                  factorAnalysisViewModel.visiblePatterns.length > 0
                ) {
                  <div class="ng-pattern-list">
                    @for (
                      pattern of factorAnalysisViewModel.visiblePatterns;
                      track pattern.patternId
                    ) {
                      <article class="ng-pattern-card">
                        <div class="ng-pattern-header">
                          <div class="ng-pattern-title-row">
                            <span class="ng-pattern-dot"></span>
                            <h3 class="ng-pattern-name">
                              {{ pattern.patternId }}.
                              {{ pattern.patternName }}
                            </h3>
                          </div>
                          <div class="ng-pattern-badges">
                            @if (
                              getPatternTypeLabel(
                                pattern.patternType,
                                pattern.hitCount
                              );
                              as patternTypeLabel
                            ) {
                              <span
                                class="ng-badge-type"
                                [class.ng-badge-type-ng-talk]="
                                  pattern.patternType === 'ng_talk'
                                "
                                [class.ng-badge-type-missing]="
                                  pattern.patternType === 'missing'
                                "
                              >
                                {{ patternTypeLabel }}
                              </span>
                            }
                            @if (isPriorityHitCount(pattern.hitCount)) {
                              <span class="ng-badge-focus">重点克服対象</span>
                            }
                            <span class="ng-badge-count ng-badge-count-active">
                              発生: {{ pattern.hitCount }}/{{
                                pattern.totalCount
                              }}回
                            </span>
                          </div>
                        </div>

                        <div class="ng-pattern-body">
                          <div class="ng-pattern-col-left">
                            <h4 class="ng-col-title">
                              直近面談での NGファクト (Why Bad)
                            </h4>
                            <div class="ng-ai-eval">
                              <span class="ng-ai-eval-badge">AI の評価</span>
                              @if (pattern.latestNgFact != null) {
                                <p class="ng-ai-eval-text">
                                  {{ pattern.latestNgFact }}
                                </p>
                              } @else {
                                <p class="ng-ai-eval-text ng-empty">
                                  ヒットなし
                                </p>
                              }
                            </div>

                            @if (
                              pattern.latestCitations != null &&
                              pattern.latestCitations.length > 0
                            ) {
                              <div class="ng-citations">
                                <span class="ng-citations-badge">
                                  実際の該当発言
                                </span>
                                @for (
                                  citation of pattern.latestCitations;
                                  track citation.lineNo
                                ) {
                                  <blockquote class="ng-citation-item">
                                    <div class="ng-citation-meta">
                                      [{{ citation.speaker }} / 該当箇所 (line
                                      {{ citation.lineNo }})]
                                    </div>
                                    <p>「{{ citation.quote }}」</p>
                                  </blockquote>
                                }
                              </div>
                            }
                          </div>

                          <div class="ng-pattern-col-right">
                            <h4 class="ng-col-title">
                              AI改善提案 (How to Good)
                            </h4>
                            @if (pattern.goodExamples.length > 0) {
                              <span class="ng-good-badge">
                                トップパフォーマーの実践好例
                              </span>
                              <div class="ng-good-examples">
                                @for (
                                  example of pattern.goodExamples;
                                  track $index
                                ) {
                                  <blockquote class="ng-good-example">
                                    <p>「{{ example }}」</p>
                                  </blockquote>
                                }
                              </div>
                            } @else {
                              <p class="ng-empty">実践好例データはありません</p>
                            }
                          </div>
                        </div>

                        @if (
                          factorAnalysisViewModel.factorAnalysis
                            .roleplaySkillMapId != null && canNavigateToRoleplay
                        ) {
                          <button
                            type="button"
                            class="ng-roleplay-button"
                            (click)="
                              navigateToRoleplay(
                                factorAnalysisViewModel.factorAnalysis
                                  .roleplaySkillMapId!
                              )
                            "
                          >
                            この型を意識してAIロープレを実施する
                          </button>
                        }
                      </article>
                    }
                  </div>
                } @else {
                  <div class="status-panel">NGなし</div>
                }
              </div>
            }
          </section>
        } @else {
          <div class="status-panel">因子マスタが未登録です</div>
        }
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .member-analysis-page {
        max-width: 1280px;
        min-width: 0;
        margin: 0 auto;
      }

      .teacher-summary {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
        margin-bottom: 16px;
      }

      .summary-card {
        padding: 20px 24px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .summary-label {
        margin: 0;
        color: #64748b;
        font-size: 12px;
        font-weight: 700;
      }

      .summary-name {
        margin: 6px 0 10px;
        color: #0f172a;
        font-size: 22px;
        font-weight: 800;
        line-height: 1.3;
      }

      .two-column-section {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 16px;
        margin-bottom: 16px;
      }

      .two-col-left,
      .two-col-right {
        display: grid;
        gap: 16px;
        align-content: start;
      }

      .factor-section {
        padding: 24px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .factor-title {
        margin: 0;
        color: #0f172a;
        font-size: 18px;
        font-weight: 800;
        line-height: 1.4;
      }

      .factor-description {
        margin: 12px 0 16px;
        color: #475569;
        font-size: 14px;
        line-height: 1.7;
      }

      .factor-description strong {
        color: #dc2626;
      }

      .factor-card {
        display: flex;
        gap: 16px;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        margin-top: 12px;
        border: 2px solid #e2e8f0;
        border-radius: 8px;
      }

      .factor-card-primary {
        border-color: #1d4ed8;
      }

      .factor-card-secondary {
        border-color: #f97316;
      }

      .factor-rank-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        font-size: 11px;
        font-weight: 800;
        border-radius: 4px;
      }

      .factor-rank-primary {
        color: #1d4ed8;
        background: #dbeafe;
      }

      .factor-rank-secondary {
        color: #ea580c;
        background: #fff7ed;
      }

      .factor-ka-name {
        margin: 6px 0 0;
        color: #0f172a;
        font-size: 18px;
        font-weight: 800;
        line-height: 1.3;
      }

      .factor-card-right {
        flex: 0 0 auto;
        text-align: right;
      }

      .factor-score-label {
        color: #64748b;
        font-size: 12px;
        font-weight: 700;
      }

      .factor-score-value {
        margin: 2px 0 0;
        color: #0f172a;
        font-size: 28px;
        font-weight: 800;
        line-height: 1.2;
      }

      .factor-score-max {
        color: #94a3b8;
        font-size: 14px;
        font-weight: 700;
      }

      .score-trend-section {
        padding: 24px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .score-trend-title {
        margin: 0 0 16px;
        color: #0f172a;
        font-size: 18px;
        font-weight: 800;
        line-height: 1.4;
      }

      .score-trend-caption {
        margin: 12px 0 0;
        color: #64748b;
        font-size: 13px;
        line-height: 1.6;
        text-align: center;
      }

      .project-progress-section {
        padding: 24px;
        margin-bottom: 16px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .project-progress-title {
        margin: 0;
        color: #0f172a;
        font-size: 18px;
        font-weight: 800;
        line-height: 1.4;
      }

      .project-progress-subtitle {
        margin: 4px 0 16px;
        color: #64748b;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.6;
      }

      .coming-soon-section {
        padding: 24px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .coming-soon-title {
        margin: 0;
        color: #0f172a;
        font-size: 18px;
        font-weight: 800;
        line-height: 1.4;
      }

      .coming-soon-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
        justify-content: center;
        min-height: 300px;
        margin-top: 16px;
        text-align: center;
        border: 2px dashed #e2e8f0;
        border-radius: 8px;
      }

      .coming-soon-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        color: #475569;
        font-size: 12px;
        font-weight: 800;
        background: #f1f5f9;
        border-radius: 4px;
      }

      .coming-soon-label {
        margin: 0;
        color: #0f172a;
        font-size: 16px;
        font-weight: 800;
        line-height: 1.4;
      }

      .coming-soon-text {
        margin: 0;
        color: #64748b;
        font-size: 13px;
        line-height: 1.6;
      }

      .summary-card-header {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: space-between;
      }

      .score-gap-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        color: #dc2626;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
        background: #fef2f2;
        border-radius: 4px;
      }

      .summary-big-number {
        margin: 12px 0 0;
        color: #0f172a;
        font-size: 36px;
        font-weight: 800;
        line-height: 1.2;
      }

      .summary-unit {
        font-size: 16px;
        font-weight: 700;
      }

      .summary-target {
        margin-left: 4px;
        color: #64748b;
        font-size: 14px;
        font-weight: 700;
      }

      .summary-empty {
        margin: 12px 0 0;
        color: #94a3b8;
        font-size: 14px;
        font-weight: 700;
      }

      h1,
      h2,
      h3,
      p {
        margin: 0;
      }

      .priority-factors {
        display: grid;
        gap: 10px;
        padding-top: 16px;
        margin-top: 16px;
        border-top: 1px solid #e2e8f0;
      }

      .priority-factors-title {
        margin: 0 0 2px;
        color: #334155;
        font-size: 14px;
        font-weight: 800;
        line-height: 1.5;
      }

      .priority-factor-item {
        display: flex;
        gap: 8px;
        align-items: center;
        min-height: 44px;
        padding: 10px 12px;
        color: #0f172a;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-left: 5px solid #eab308;
        border-radius: 8px;
      }

      .priority-factor-item.rank-1 {
        border-left-color: #ef4444;
      }

      .priority-factor-item.rank-2 {
        border-left-color: #f97316;
      }

      .priority-factor-item.rank-3 {
        border-left-color: #eab308;
      }

      .priority-factor-rank {
        flex: 0 0 auto;
        color: #475569;
        font-size: 14px;
        font-weight: 800;
      }

      .priority-factor-name {
        min-width: 0;
        font-size: 14px;
        font-weight: 800;
        line-height: 1.5;
        overflow-wrap: anywhere;
      }

      .bridge-guide {
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: center;
        padding: 16px 0;
      }

      .bridge-text {
        margin: 0;
        padding: 8px 20px;
        color: #475569;
        font-size: 13px;
        font-weight: 700;
        border: 1px solid #e2e8f0;
        border-radius: 999px;
      }

      .bridge-arrow {
        color: #94a3b8;
        font-size: 18px;
      }

      .ng-analysis-section {
        margin-bottom: 24px;
      }

      .ng-analysis-header {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        margin-bottom: 20px;
      }

      .ng-analysis-dot {
        display: block;
        flex: 0 0 auto;
        width: 14px;
        height: 14px;
        margin-top: 5px;
        background: #ef4444;
        border-radius: 50%;
      }

      .ng-analysis-title {
        margin: 0;
        color: #0f172a;
        font-size: 22px;
        font-weight: 800;
        line-height: 1.3;
      }

      .ng-analysis-subtitle {
        margin: 4px 0 0;
        color: #64748b;
        font-size: 13px;
        font-weight: 500;
      }

      .factor-group {
        margin-top: 16px;
      }

      .factor-group-header {
        display: flex;
        gap: 12px;
        align-items: center;
        padding: 16px 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .factor-group-badge {
        display: inline-flex;
        align-items: center;
        min-height: 28px;
        padding: 4px 12px;
        color: #475569;
        font-size: 13px;
        font-weight: 800;
        white-space: nowrap;
        background: #f1f5f9;
        border-radius: 6px;
      }

      .factor-group-badge.factor-rank-primary {
        color: #1d4ed8;
        background: #dbeafe;
      }

      .factor-group-badge.factor-rank-secondary {
        color: #ea580c;
        background: #fff7ed;
      }

      .factor-group-name {
        color: #0f172a;
        font-size: 18px;
        font-weight: 800;
      }

      .ng-pattern-list {
        display: grid;
        gap: 16px;
        margin-top: 16px;
      }

      .ng-pattern-card {
        padding: 24px;
        background: #ffffff;
        border: 2px solid #fca5a5;
        border-radius: 12px;
      }

      .ng-pattern-header {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        justify-content: space-between;
        padding-bottom: 16px;
        border-bottom: 1px solid #e2e8f0;
      }

      .ng-pattern-title-row {
        display: flex;
        gap: 10px;
        align-items: center;
      }

      .ng-pattern-dot {
        display: block;
        flex: 0 0 auto;
        width: 12px;
        height: 12px;
        background: #ef4444;
        border-radius: 50%;
      }

      .ng-pattern-name {
        margin: 0;
        color: #0f172a;
        font-size: 16px;
        font-weight: 800;
        line-height: 1.4;
      }

      .ng-pattern-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        flex: 0 0 auto;
        justify-content: flex-end;
      }

      .ng-badge-type {
        display: inline-flex;
        align-items: center;
        min-height: 26px;
        padding: 2px 10px;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
        border-radius: 4px;
      }

      .ng-badge-type-ng-talk {
        color: #be123c;
        background: #fff1f2;
        border: 1px solid #fecdd3;
      }

      .ng-badge-type-missing {
        color: #1d4ed8;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
      }

      .ng-badge-focus {
        display: inline-flex;
        align-items: center;
        min-height: 26px;
        padding: 2px 10px;
        color: #dc2626;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
        background: #ffffff;
        border: 1px solid #fca5a5;
        border-radius: 4px;
      }

      .ng-badge-count {
        display: inline-flex;
        align-items: center;
        min-height: 26px;
        padding: 2px 10px;
        color: #475569;
        font-size: 12px;
        font-weight: 800;
        white-space: nowrap;
        background: #f1f5f9;
        border-radius: 4px;
      }

      .ng-badge-count-active {
        color: #dc2626;
        background: #fef2f2;
      }

      .ng-pattern-body {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        padding-top: 16px;
      }

      .ng-col-title {
        margin: 0 0 12px;
        color: #0f172a;
        font-size: 14px;
        font-weight: 800;
      }

      .ng-ai-eval {
        margin-bottom: 16px;
      }

      .ng-ai-eval-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        color: #ffffff;
        font-size: 11px;
        font-weight: 800;
        background: #0f172a;
        border-radius: 4px;
      }

      .ng-ai-eval-text {
        margin: 8px 0 0;
        color: #0f172a;
        font-size: 14px;
        line-height: 1.7;
        white-space: pre-wrap;
      }

      .ng-citations {
        margin-top: 8px;
      }

      .ng-citations-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        color: #ffffff;
        font-size: 11px;
        font-weight: 800;
        background: #ef4444;
        border-radius: 4px;
      }

      .ng-citation-item {
        padding: 12px 14px;
        margin: 8px 0 0;
        background: #fef2f2;
        border: 0;
        border-left: 4px solid #ef4444;
        border-radius: 8px;
      }

      .ng-citation-meta {
        color: #64748b;
        font-size: 12px;
        font-weight: 700;
      }

      .ng-citation-item p {
        margin: 4px 0 0;
        color: #0f172a;
        font-size: 14px;
        line-height: 1.7;
        white-space: pre-wrap;
      }

      .ng-good-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        color: #ffffff;
        font-size: 11px;
        font-weight: 800;
        background: #16a34a;
        border-radius: 4px;
      }

      .ng-good-examples {
        display: grid;
        gap: 8px;
        margin-top: 8px;
      }

      .ng-good-example {
        padding: 12px 14px;
        margin: 0;
        background: #fffbeb;
        border: 0;
        border-left: 4px solid #f59e0b;
        border-radius: 8px;
      }

      .ng-good-example p {
        margin: 0;
        color: #0f172a;
        font-size: 14px;
        line-height: 1.7;
        white-space: pre-wrap;
      }

      .ng-empty {
        margin: 8px 0 0;
        color: #94a3b8;
        font-size: 14px;
      }

      .ng-roleplay-button {
        display: block;
        width: 100%;
        min-height: 48px;
        margin-top: 16px;
        padding: 0 18px;
        color: #ffffff;
        font-size: 14px;
        font-weight: 800;
        cursor: pointer;
        background: #6366f1;
        border: 0;
        border-radius: 8px;
      }

      .ng-roleplay-button:hover {
        background: #4f46e5;
      }

      .status-panel {
        padding: 28px 24px;
        color: #475569;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.7;
        text-align: center;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .status-panel.error {
        color: #b91c1c;
        background: #fef2f2;
        border-color: #fecaca;
      }

      @media (width < 700px) {
        .teacher-summary,
        .two-column-section {
          grid-template-columns: 1fr;
        }

        .ng-pattern-body {
          grid-template-columns: 1fr;
        }

        .ng-pattern-header {
          flex-wrap: wrap;
        }

        .summary-card,
        .ng-pattern-card {
          padding: 16px;
        }
      }
    `,
  ],
})
export class TrygroupMemberAnalysisPageComponent implements OnInit {
  readonly FactorAnalysisStatus = TrygroupFactorAnalysisStatus
  readonly getFactorRankLabel = resolveFactorRankLabel
  readonly getKaIdentifier = resolveKaIdentifier
  readonly getPatternTypeLabel = resolvePatternTypeLabel
  readonly isPriorityHitCount = resolveIsPriorityHitCount
  analysis: TrygroupPersonalAnalysis | null = null
  factorAnalysisViewModels: FactorAnalysisViewModel[] = []
  diagnosisFactorAnalyses: TrygroupFactorAnalysis[] = []
  isLoading = true
  errorMessage = ''
  canNavigateToRoleplay = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private trygroupPersonalAnalysisService: TrygroupPersonalAnalysisService,
    private userStore: UserStore,
    private roleplayStore: RoleplayStore,
  ) {}

  ngOnInit(): void {
    const userId = this.route.snapshot.paramMap.get('userId')
    if (userId == null) {
      throw new Error('userId route parameter is required')
    }

    this.userStore
      .getOneSelf({ useCache: true })
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        const roleplayAllowedRoles = ['SUPER_ADMIN', 'ENABLER']
        this.canNavigateToRoleplay = user.roles.some((role) =>
          roleplayAllowedRoles.includes(role.code),
        )
      })

    this.trygroupPersonalAnalysisService
      .getPersonalAnalysis(userId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (analysis) => {
          const factorAnalyses = [...analysis.factorAnalyses].sort(
            (a, b) => a.rank - b.rank,
          )
          this.analysis = { ...analysis, factorAnalyses }
          this.factorAnalysisViewModels =
            this.buildFactorAnalysisViewModels(factorAnalyses)
          this.diagnosisFactorAnalyses = factorAnalyses
            .filter(
              (factorAnalysis) =>
                factorAnalysis.status === TrygroupFactorAnalysisStatus.Ready &&
                (factorAnalysis.rank === 1 || factorAnalysis.rank === 2),
            )
            .map((factorAnalysis) => ({
              ...factorAnalysis,
              patternSummaries: [...factorAnalysis.patternSummaries].sort(
                (a, b) => a.patternId - b.patternId,
              ),
            }))
          this.isLoading = false
          this.errorMessage = ''
        },
        error: () => {
          this.analysis = null
          this.factorAnalysisViewModels = []
          this.diagnosisFactorAnalyses = []
          this.isLoading = false
          this.errorMessage = 'データの取得に失敗しました'
        },
      })
  }

  /** KAに対応するペルソナを特定し、ロープレセッション開始画面に遷移する。 */
  navigateToRoleplay(roleplaySkillMapId: string): void {
    this.roleplayStore
      .getPersonas()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (personas) => {
          const matched = personas.find(
            (p) => p.skillMapId === roleplaySkillMapId,
          )
          if (matched) {
            this.router.navigate(['/roleplay/session'], {
              queryParams: { personaId: matched.id },
            })
          } else {
            this.router.navigate(['/roleplay/session'])
          }
        },
        error: () => {
          this.router.navigate(['/roleplay/session'])
        },
      })
  }

  formatNoKaDefinitionMessage(factorAnalysis: TrygroupFactorAnalysis): string {
    return `KA${factorAnalysis.kaId}のNG分析定義が未登録です`
  }

  getKaScore(kaId: number): string {
    const identifier = this.getKaIdentifier(kaId)
    const score = this.analysis?.radarChart?.personalScores.find(
      (s) => s.identifier === identifier,
    )

    return score ? score.score.toFixed(1) : '-'
  }

  formatScore(value: number): string {
    return value.toFixed(1)
  }

  formatPercent(value: number): string {
    return (value * 100).toFixed(1)
  }

  private getPatternDisplayLimit(rank: number): number {
    return rank === 2 ? 2 : 3
  }

  private buildFactorAnalysisViewModels(
    factorAnalyses: TrygroupFactorAnalysis[],
  ): FactorAnalysisViewModel[] {
    return factorAnalyses.map((factorAnalysis) => ({
      factorAnalysis,
      visiblePatterns: this.getVisiblePatterns(factorAnalysis),
    }))
  }

  private getVisiblePatterns(
    factorAnalysis: TrygroupFactorAnalysis,
  ): TrygroupFactorAnalysis['patternSummaries'] {
    return factorAnalysis.patternSummaries
      .filter((pattern) => pattern.hitCount >= 1)
      .sort((a, b) => {
        if (a.hitCount !== b.hitCount) {
          return b.hitCount - a.hitCount
        }

        return a.patternId - b.patternId
      })
      .slice(0, this.getPatternDisplayLimit(factorAnalysis.rank))
      .map((pattern) => ({
        ...pattern,
        goodExamples: pattern.goodExamples.slice(
          0,
          GOOD_EXAMPLES_DISPLAY_LIMIT,
        ),
      }))
  }

  calculateDayN(progress: ProjectProgress): number {
    const today = dateOnlyToTimestamp(toTodayDateOnlyInJst())
    const kickoff = dateOnlyToTimestamp(progress.kickoffDate)
    const diff = Math.floor((today - kickoff) / DAY_MS)
    return Math.max(0, diff)
  }

  calculateRemainingDays(progress: ProjectProgress): number {
    const today = dateOnlyToTimestamp(toTodayDateOnlyInJst())
    const end = dateOnlyToTimestamp(progress.endDate)
    const diff = Math.floor((end - today) / DAY_MS)
    return Math.max(0, diff)
  }
}

function dateOnlyToTimestamp(dateOnly: string): number {
  const [year, month, day] = dateOnly.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

function toTodayDateOnlyInJst(): string {
  const jstToday = new Date(Date.now() + JST_OFFSET_MS)
  return `${jstToday.getUTCFullYear()}-${String(
    jstToday.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(jstToday.getUTCDate()).padStart(2, '0')}`
}
